import React from "react";
import _ from "lodash";

import Graph from "src/library/components/Graph";
import {
  PendingCustomersResult,
  useWidgetDataApi
} from "src/core/dashboard/api/widget-data.api";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { GraphPayload } from "src/library/components/Graph/GraphToolTip";
import { useWidgetContainerSize } from "src/core/dashboard/hooks";
import { WidgetDisplayTypes } from "src/core/dashboard/api/dashboard.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";
import useWidgetTypes from "src/core/dashboard/hooks/useWidgetTypes";

import {
  getDisplayRange,
  getGraphMetrics,
  DisplayRangeType,
  getSelectedWidgetView,
  WidgetProps
} from "../utils";
import DataLoadError from "../DataLoadError";
import { NumberWidget } from "../NumberWidget";
import { WidgetInnerHeader } from "../WidgetInnerHeader";
import { useWidgetFontSize } from "../hooks";
import { groupTotalPendingCustomers } from "./utils";

// Type for the flat object used to display data in graph
export type PendingCustomersGraphValue = Omit<GraphPayload, "date"> & {
  date?: Date;
  value?: number;
} & Partial<{ [customGroupKey: string]: string | Date | number }>;

const PENDING_CUSTOMERS_CACHE_KEY = "widget-pending-customers";

/**
 * Returns pending customer widget content
 */
export const PendingCustomersWidgetContent: React.FunctionComponent<WidgetProps & { wrapperRef: React.RefObject<HTMLDivElement> }> = ({
  options, filters, widgetId, widgetTypeId, terminology, wrapperRef, ignoreCache = false
}) => {
  const widgetTypes = useWidgetTypes();
  const [ displayRange, setDisplayRange ] = React.useState<DisplayRangeType>("day");
  const [ pendingCustomersDataResponse, pendingCustomersDataRequest ] = useWidgetDataApi("GET_PENDING_CUSTOMER_TOTALS");
  const { width } = useWidgetContainerSize(wrapperRef);
  const fontSize = useWidgetFontSize(width, options.view);
  const widgetType = _.find(widgetTypes, wt => wt.id === widgetTypeId);
  const [ xAxis, setxAxis ] = React.useState("date");

  const selectedView = React.useMemo(() => {
    return getSelectedWidgetView(options.view);
  }, [ options ]);

  // On load, get the data we need
  React.useEffect(() => {
    pendingCustomersDataRequest({
      widgetTypeId,
      filters,
      options: null
    }, {
      cache: {
        maxAge: cacheMinutesToMilliseconds(60),
        key: () => `${PENDING_CUSTOMERS_CACHE_KEY}-${widgetTypeId}`,
        ignoreCache
      }
    });
  }, [
    filters,
    ignoreCache,
    pendingCustomersDataRequest,
    widgetTypeId
  ]);

  const groupByKeyFormatted = widgetType?.options.groupByKey?.includes("custom:") ? widgetType?.options.groupByKey.split("custom:")[ 1 ] : widgetType?.options.groupByKey || "date";

  // Takes the raw response from API and transforms the result to the shape required for graph data.
  // During this process, we also sets the displayRange
  const transformToGraphData = React.useCallback((pendingCustomersResult: PendingCustomersResult): PendingCustomersGraphValue[] => {
    // Find range to determine whether to display in days, weeks or months
    const calculatedDisplayRange = getDisplayRange(pendingCustomersResult);

    // Update state required by Graph component
    setDisplayRange(calculatedDisplayRange);
    const xAxis = options.view === WidgetDisplayTypes.BarGraph && calculatedDisplayRange === "day" ? groupByKeyFormatted : "date";

    // Group, accumulate totals and return
    setxAxis(xAxis);
    const data = groupTotalPendingCustomers(pendingCustomersResult, xAxis, groupByKeyFormatted, calculatedDisplayRange);

    return data;
  }, [ groupByKeyFormatted, options.view ]);

  // The data used to graph view widget
  const graphDisplayData = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      if (pendingCustomersDataResponse.data?.result) {
        return transformToGraphData(pendingCustomersDataResponse.data?.result);
      }
    }

    return null;
  }, [
    pendingCustomersDataResponse.data?.result,
    transformToGraphData,
    selectedView
  ]);

  const graphMetrics = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      return graphDisplayData ? getGraphMetrics(graphDisplayData, [ xAxis ]) : [];
    }

    return null;
  }, [
    graphDisplayData,
    selectedView,
    xAxis
  ]);

  // The data used to display number metrics
  const numberDisplayData = React.useMemo(() => {
    const pendingCustomersResult = pendingCustomersDataResponse.data?.result;

    if (selectedView === WidgetDisplayTypes.Number && pendingCustomersResult) {
      if (groupByKeyFormatted !== "date") {
        if (pendingCustomersResult) {
          const customersByGroup = groupTotalPendingCustomers(pendingCustomersResult, groupByKeyFormatted, groupByKeyFormatted) as Array<{ value: number; count: 0 } & {[key: string]: string;}>;

          return {
            value: customersByGroup.reduce((value, pendingCustomer) => value + pendingCustomer.value, 0),
            data: customersByGroup.map(typeData => {
              return {
                group: _.startCase(typeData[ groupByKeyFormatted ].replace(/[_-]/g, " ")),
                value: typeData.value
              };
            })
          };
        }
      } else {
        return { value: pendingCustomersResult.reduce((total, pendingCustomers) => total + pendingCustomers.count, 0) };
      }
    }

    return null;
  }, [
    groupByKeyFormatted,
    pendingCustomersDataResponse.data?.result,
    selectedView
  ]);

  if (selectedView !== "bar" && selectedView !== "line" && selectedView !== WidgetDisplayTypes.Number) {
    // Currently, graph bar and line are the only handled view options availble below
    return null;
  }

  return (
    <>
      {pendingCustomersDataResponse.pending && (
        <LoadingSpinner type="circular" />
      )}

      {graphDisplayData && graphMetrics && !pendingCustomersDataResponse.error && (selectedView === "line" || selectedView === "bar") && (
        <>
          <WidgetInnerHeader title={`New ${_.startCase(terminology?.customer.plural.text)}`} />

          <Graph
            containerWidth={width && width}
            hideLegend={xAxis !== "date"}
            stacked={xAxis === "date"}
            data={graphDisplayData}
            type={selectedView}
            toolTipValueType="number"
            graphMetrics={graphMetrics}
            xAxis={{
              dataKey: xAxis,
              dateFormat: displayRange === "month" ? "month" : "day"
            }}
          />
        </>
      )}

      {numberDisplayData && fontSize && !pendingCustomersDataResponse.error && (
        <NumberWidget
          widgetId={widgetId}
          displayData={numberDisplayData}
          units={`New ${_.startCase(terminology?.customer.plural.text)}`}
          valueType="number"
          fontSize={fontSize}
          hideToolTip="header"
        />
      )}

      {pendingCustomersDataResponse.error && (
        <DataLoadError errorMessage={pendingCustomersDataResponse.error.message} />
      )}

    </>
  );
};
